import { isPatientEmbedProductionHost } from "../../utils/patient-embed-environment";

import type {
  MalnutritionCriterion,
  MalnutritionFeedbackPayload,
  MalnutritionFeedback,
  MalnutritionRiskAssessment,
  MalnutritionStatus,
  PatientWeightHistory,
} from "../../types/Malnutrition";

// Mocks are controlled by the `mock` query param (no rebuild needed, unlike an
// env var). Default is off (real API); add `?mock=true` (or `mock=1`) for mocks.
export const malnutritionUseMocks = (): boolean => {
  if (typeof window === "undefined") return false; // tests / SSR: default to real API
  // Never serve fabricated patient data on a production host, even if the URL
  // asks for it — the ?mock= toggle is for local / QA / staging only.
  if (isPatientEmbedProductionHost()) return false;
  const raw = new URLSearchParams(window.location.search).get("mock");
  return raw === "true" || raw === "1";
};

const MOCK_LATENCY_MS = 120;

const resolve = <T>(value: T): Promise<T> =>
  new Promise((res) => setTimeout(() => res(value), MOCK_LATENCY_MS));

const ALL_STATUSES: MalnutritionStatus[] = [
  "high_risk",
  "medium_risk",
  "protocol",
  "following",
  "consult",
];

const readStatusFromUrl = (): MalnutritionStatus | null => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("status");
  if (!raw) return null;
  return (ALL_STATUSES as string[]).includes(raw)
    ? (raw as MalnutritionStatus)
    : null;
};

// EDDs are relative so the discharge chip keeps exercising the same branch
// whenever the fixtures are viewed: today -> "Discharge today", +1 day -> the
// dated variant, +4 days -> too far out to chip, null -> no EDD in PE.
const mockEDD = (daysFromToday: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

const PATIENT_BY_STATUS: Record<
  MalnutritionStatus,
  MalnutritionRiskAssessment["patient"]
> = {
  high_risk: {
    full_name: "Harden Hennepin",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
    edd: mockEDD(0),
  },
  medium_risk: {
    full_name: "Andrew Doughty",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
    edd: mockEDD(1),
  },
  protocol: {
    full_name: "Anna Breckenridge",
    mrn: "12345678",
    current_bed: "412A",
    age: 55,
    gender: "Female",
    current_unit: "4 East",
    edd: null,
  },
  following: {
    full_name: "Andrew Granda",
    mrn: "12345678",
    current_bed: "412A",
    age: 68,
    gender: "Male",
    current_unit: "4 East",
    edd: mockEDD(4),
  },
  consult: {
    full_name: "Anna Caldera",
    mrn: "12345678",
    current_bed: "412A",
    age: 58,
    gender: "Female",
    current_unit: "4 East",
    edd: mockEDD(1),
  },
};

// Shapes/values below mirror what the backend serializer actually emits (see
// api/serializers.py _malnutrition_criterion_to_fe): metric evidence ->
// Current/Prior/Change rows (raw values, no unit, "M/D" dates); signal/text
// evidence -> the "metric" kind with a single unlabeled row; quote evidence ->
// empty source_kind (not in the proto).
const FLAGGED_CRITERIA: MalnutritionCriterion[] = [
  {
    kind: "metric",
    title: "Significant weight loss",
    rows: [
      { label: "Current", value: "117.7 · 4/29" },
      { label: "Prior", value: "133.2 · 1/29" },
      { label: "Change", value: "-11.43%" },
    ],
    source: "model",
    fact: "significant_weight_loss",
  },
  {
    // signal evidence -> single unlabeled row
    kind: "metric",
    title: "Low BMI",
    rows: [{ label: "", value: "Patient has BMI of 15.9" }],
    source: "model",
    fact: "low_bmi",
  },
  {
    kind: "quote",
    title: "Severe muscle depletion",
    quote: "Nutrition focused physical exam: severe muscle depletion.",
    source_kind: "",
    source_author: "Amber M Cree, RD",
    source_timestamp: "4/29 8:12 AM",
    source: "model",
    fact: "severe_muscle_depletion",
  },
];

const PROTOCOL_CRITERIA: MalnutritionCriterion[] = [
  {
    // text evidence -> single unlabeled row
    kind: "metric",
    title: "Length of stay exceeds 7 days",
    rows: [{ label: "", value: "Current LOS: 9 days · Admitted 4/20" }],
    source: "protocol",
    fact: "length_of_stay",
  },
  {
    kind: "metric",
    title: "NPO duration exceeds 5 days",
    rows: [{ label: "", value: "NPO since 4/23 · 6 days" }],
    source: "protocol",
    fact: "npo_duration",
  },
  {
    kind: "metric",
    title: "Active nutrition consult",
    rows: [
      { label: "", value: "Ordered 4/22 · Malnutrition screening · Pending" },
    ],
    source: "protocol",
    fact: "nutrition_consult",
  },
];

const CRITERIA_BY_STATUS: Record<MalnutritionStatus, MalnutritionCriterion[]> =
  {
    // high_risk mixes model + protocol criteria to exercise both the
    // "Why Flagged" and "Protocol" sections at once.
    high_risk: [...FLAGGED_CRITERIA, ...PROTOCOL_CRITERIA],
    medium_risk: FLAGGED_CRITERIA.slice(0, 2),
    protocol: PROTOCOL_CRITERIA,
    following: FLAGGED_CRITERIA.slice(0, 2),
    consult: FLAGGED_CRITERIA.slice(0, 3),
  };

const WEIGHT_HISTORY: PatientWeightHistory = {
  readings: [
    {
      date: "4/29",
      reading_ts: "2026-04-29T14:00:00Z",
      weight_lb: 102,
      source: "Standing",
    },
    {
      date: "4/20",
      reading_ts: "2026-04-20T14:00:00Z",
      weight_lb: 108,
      source: "Bed",
    },
    {
      date: "4/12",
      reading_ts: "2026-04-12T14:00:00Z",
      weight_lb: 110,
      source: "Standing",
    },
    {
      date: "4/05",
      reading_ts: "2026-04-05T14:00:00Z",
      weight_lb: 113,
      source: "Bed",
    },
    {
      date: "3/28",
      reading_ts: "2026-03-28T14:00:00Z",
      weight_lb: 116,
      source: "Standing",
    },
    {
      date: "3/15",
      reading_ts: "2026-03-15T14:00:00Z",
      weight_lb: 119,
      source: "Outpatient",
    },
    {
      date: "2/20",
      reading_ts: "2026-02-20T14:00:00Z",
      weight_lb: 121,
      source: "Outpatient",
    },
    {
      date: "1/10",
      reading_ts: "2026-01-10T14:00:00Z",
      weight_lb: 123,
      source: "Outpatient",
    },
  ],
};

// In mock mode there's no backend, so remember the last recorded answer here;
// the card reads it back after the risk query is invalidated and refetched.
let mockFeedback: MalnutritionFeedback | null = null;

// Mirrors the server's partial update rather than replacing the row: every
// request carries the answer, the Yes/No click omits the follow-up (which is
// left as it was), and a "yes" retires the follow-up. Worth keeping faithful —
// it is what makes mock mode exercise the real edit behaviour instead of a
// simplified version of it.
export const mockSetMalnutritionFeedback = (
  payload: MalnutritionFeedbackPayload
): void => {
  const current: MalnutritionFeedback = mockFeedback ?? {
    is_good_candidate: payload.is_good_candidate,
    rejected_criteria: [],
    comment: null,
    updated_by_name: "Casey Mercer, RD",
  };
  const next: MalnutritionFeedback = {
    ...current,
    is_good_candidate: payload.is_good_candidate,
    updated_by_name: "Casey Mercer, RD",
  };
  if (payload.rejected_criteria !== undefined) {
    next.rejected_criteria = payload.rejected_criteria;
  }
  if (payload.comment !== undefined) next.comment = payload.comment;
  if (next.is_good_candidate) {
    next.rejected_criteria = [];
    next.comment = null;
  }
  mockFeedback = next;
};

// Between test cases, and whenever a reviewer wants the card back to unanswered.
export const mockResetMalnutritionFeedback = (): void => {
  mockFeedback = null;
};

// `?criteria=none` empties criteria_met while leaving everything else — status
// included — exactly as it was. The flagged chrome still renders, so the Yes/No
// control is there and answering No opens the criteria feedback panel on its
// empty state: no checkboxes, just the comment box. Without the param this is
// inert, so no existing mock behaviour shifts.
//
// It stacks with ?status=, and since status already defaults to high_risk,
// `?mock=true&criteria=none` alone gives the high-risk patient with no criteria.
const criteriaSuppressedByUrl = (): boolean =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("criteria") === "none";

const buildRiskAssessment = (
  status: MalnutritionStatus
): MalnutritionRiskAssessment => ({
  patient: PATIENT_BY_STATUS[status],
  status,
  criteria_met: criteriaSuppressedByUrl() ? [] : CRITERIA_BY_STATUS[status],
  feedback: mockFeedback,
});

// Summary header for an unflagged patient (viewable via ?status=none): the
// header renders but there's no status banner / criteria — mirrors the API
// returning the patient summary with status=null.
const UNFLAGGED_PATIENT: MalnutritionRiskAssessment["patient"] = {
  full_name: "Jordan Vance",
  mrn: "12345678",
  current_bed: "412A",
  age: 61,
  gender: "Female",
  current_unit: "4 East",
  // No status, but a discharge today — the chip row carries this chip alone.
  edd: mockEDD(0),
};

export const mockGetMalnutritionRiskAssessment = (
  _emrPatientID: string
): Promise<MalnutritionRiskAssessment> => {
  const rawStatus =
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("status");
  if (rawStatus === "none") {
    return resolve({
      patient: UNFLAGGED_PATIENT,
      status: null,
      criteria_met: [],
      feedback: mockFeedback,
    });
  }
  const status = readStatusFromUrl() ?? "high_risk";
  return resolve(buildRiskAssessment(status));
};

// `?weights=close` swaps in two readings 48h apart (ENG-31026), under the
// model's 96h minimum gap, so the card shows the table with no trend, range
// selector or marked rows. Stacks with the other params.
const CLOSE_WEIGHT_HISTORY: PatientWeightHistory = {
  readings: [
    {
      date: "4/29",
      reading_ts: "2026-04-29T14:00:00Z",
      weight_lb: 160,
      source: "Standing",
    },
    {
      date: "4/27",
      reading_ts: "2026-04-27T14:00:00Z",
      weight_lb: 170,
      source: "Bed",
    },
  ],
};

const closeWeightsByUrl = (): boolean =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("weights") === "close";

export const mockGetPatientWeights = (
  _emrPatientID: string
): Promise<PatientWeightHistory> =>
  resolve(closeWeightsByUrl() ? CLOSE_WEIGHT_HISTORY : WEIGHT_HISTORY);
